import { ref } from 'vue'
import { fetchTossCheckout } from '@/api/mallOrder'

const SDK_SRC = 'https://js.tosspayments.com/v2/standard'

interface TossAmount {
  currency: 'KRW'
  value: number
}

interface TossWidgets {
  setAmount(amount: TossAmount): Promise<void>
  renderPaymentMethods(options: { selector: string; variantKey: string }): Promise<unknown>
  renderAgreement(options: { selector: string; variantKey: string }): Promise<unknown>
  requestPayment(options: {
    orderId: string
    orderName: string
    successUrl: string
    failUrl: string
    customerEmail?: string
    customerName?: string
  }): Promise<void>
}

interface TossPaymentsFactory {
  (clientKey: string): {
    widgets(options: { customerKey: string }): TossWidgets
  }
}

declare global {
  interface Window {
    TossPayments?: TossPaymentsFactory
  }
}

let sdkPromise: Promise<void> | null = null

function loadTossSdk() {
  if (window.TossPayments) return Promise.resolve()
  if (sdkPromise) return sdkPromise
  sdkPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = SDK_SRC
    script.async = true
    script.dataset.tossSdk = '1'
    script.onload = () => resolve()
    script.onerror = () => {
      sdkPromise = null
      reject(new Error('결제창을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.'))
    }
    document.head.appendChild(script)
  })
  return sdkPromise
}

export function useTossPayment() {
  const ready = ref(false)
  const testMode = ref(false)
  const preparing = ref(false)
  let widgets: TossWidgets | null = null
  let clientKey = ''
  let customerKey = ''

  async function mount(selectors: { methods: string; agreement: string }, amount: number) {
    preparing.value = true
    try {
      if (!clientKey) {
        const config = await fetchTossCheckout()
        testMode.value = config.testMode
        clientKey = config.clientKey
        customerKey = config.customerKey
      }
      await loadTossSdk()
      if (!window.TossPayments) {
        throw new Error('결제창을 불러오지 못했습니다.')
      }

      const methodEl = document.querySelector(selectors.methods)
      const agreementEl = document.querySelector(selectors.agreement)
      if (!methodEl || !agreementEl) {
        ready.value = false
        throw new Error('결제창을 불러오지 못했습니다. 새로고침해 주세요.')
      }

      const painted = methodEl.childElementCount > 0 && agreementEl.childElementCount > 0
      if (!widgets || !painted) {
        widgets = window.TossPayments(clientKey).widgets({ customerKey })
        await widgets.setAmount({ currency: 'KRW', value: amount })
        await Promise.all([
          widgets.renderPaymentMethods({
            selector: selectors.methods,
            variantKey: 'DEFAULT',
          }),
          widgets.renderAgreement({
            selector: selectors.agreement,
            variantKey: 'AGREEMENT',
          }),
        ])
      } else {
        await widgets.setAmount({ currency: 'KRW', value: amount })
      }
      ready.value = true
    } catch (error) {
      ready.value = false
      throw error
    } finally {
      preparing.value = false
    }
  }

  async function pay(payload: {
    orderId: string
    orderName: string
    amount: number
    customerName?: string
    customerEmail?: string
  }) {
    if (!widgets) {
      throw new Error('결제창을 아직 불러오지 못했습니다.')
    }
    await widgets.setAmount({ currency: 'KRW', value: payload.amount })
    const origin = window.location.origin
    await widgets.requestPayment({
      orderId: payload.orderId,
      orderName: payload.orderName,
      successUrl: `${origin}/mall/checkout/toss-success`,
      failUrl: `${origin}/mall/checkout/toss-fail?orderId=${encodeURIComponent(payload.orderId)}`,
      customerName: payload.customerName,
      customerEmail: payload.customerEmail || undefined,
    })
  }

  return { ready, testMode, preparing, mount, pay }
}
